<template>
  <div class="w-full flex flex-col justify-center gap-2" @click.stop>
    <div class="normal-case leading-5">
      {{ program.poll_titles?.[poll.id] || poll.title || poll.name }}
    </div>
    <div class="flex gap-2">
      <button
        v-for="entry in poll.entries"
        :key="entry.id"
        :disabled="submittingEntryId !== null"
        class="flex items-center justify-center rounded-xl bg-primary px-2 h-7 text-white whitespace-nowrap disabled:opacity-50"
        @click.stop="submit(entry)"
      >
        <Loader2 v-if="submittingEntryId === entry.id" class="w-4 h-4 animate-spin" />
        <template v-else>
          {{ entry.label }}
        </template>
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {ref} from "vue";
import {Loader2} from "@lucide/vue";
import {Poll, PollEntry, ProgramSnapshot} from "@/program/interfaces/Program";
import Consumer from "@/core/interfaces/consumers/Consumer";
import usePollSubmission from "@/program/composables/usePollSubmission";
import {pollEntryActions, pollEntryDeclaredInformation} from "@/core/http/resources/PollResource";
import {SmokingStatus} from "@/customer/interfaces/DeclaredInformation";
import {useNotificationStore} from "@/stores/notification.store";

const props = defineProps<{
    /** The poll to display in place of the program teaser. */
    poll: Poll;
    /** The program the poll belongs to. */
    program: ProgramSnapshot;
    /** The consumer answering the poll. */
    consumer: Consumer;
}>();

const emit = defineEmits(['answered', 'hidden', 'show-program']);

const {submitPollEntry} = usePollSubmission();
const notificationStore = useNotificationStore();

const submittingEntryId = ref<number | null>(null);

/** The smoking status the entry declares on the consumer, when it declares one. */
const declaredSmoking = (entry: PollEntry): SmokingStatus | null =>
    (pollEntryDeclaredInformation(entry)?.smoking as SmokingStatus | undefined) ?? null;

/**
 * Swap back to the regular teaser action as soon as possible: immediately
 * for entries without action, as soon as the action responds otherwise —
 * the answer recording is slow (it refreshes the program statuses) and
 * completes in the background. When the entry action creates a feedback
 * hiding the program, the card is hidden as well.
 */
const submit = (entry: PollEntry) => {
    if (submittingEntryId.value !== null) {
        return;
    }

    const smoking = declaredSmoking(entry);

    if (pollEntryActions(entry).length) {
        submittingEntryId.value = entry.id;
    } else {
        emit('answered');
    }

    submitPollEntry(props.poll, entry, props.consumer, (responses) => {
        submittingEntryId.value = null;

        const hidden = responses.some(
            response => (response as { hide_notification?: boolean })?.hide_notification
        );

        if (hidden) {
            emit('hidden');
            window.ipcRenderer.hideProgram(props.consumer.key, props.program.id);
        }

        if (smoking) {
            notificationStore.setDeclaredSmoking(props.consumer.key, smoking);
        }

        // The patient was just declared smoker: open the program on the main
        // window so the pharmacist carries on with the questionnaire. An answer
        // that hides the card has nothing to open.
        if (!hidden && smoking === SmokingStatus.SMOKER) {
            emit('show-program');
        }

        emit('answered');
    }).catch(() => {
        submittingEntryId.value = null;
    });
};
</script>
